import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  /* Unified smooth animation for all content */
  .hero-content {
    opacity: 0;
    transform: translateY(20px);
    transition: all 400ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: transform, opacity;

    &.loaded {
      opacity: 1;
      transform: translateY(0px);
    }
  }

  /* Restore original text hierarchy and spacing */
  .hero-name {
    margin: 0 0 30px 4px;

    .big-heading {
      color: var(--lightest-slate);
      font-weight: 600; /* Make Mirza Waleed bold */
      line-height: 1.1;
    }

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  .hero-subtitle {
    margin: 5px 0 0 0;

    .big-heading {
      color: var(--slate);
      font-weight: 400; /* Keep subtitle normal weight */
      line-height: 0.9;
      font-size: clamp(20px, 8vw, 40px); /* Smaller than main heading */
    }
  }

  .hero-cta {
    margin: 50px 0 0 0; /* Original button spacing */
  }

  /* Original responsive font sizes */
  h1 {
    margin: 0 0 30px 4px;
    color: var(--white-new);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsLoaded(true);
      return;
    }

    // Single, smooth animation trigger - no layout shift
    const timeout = setTimeout(() => {
      setIsLoaded(true);
    }, 100); // Very quick start for professional feel

    return () => clearTimeout(timeout);
  }, [prefersReducedMotion]);

  return (
    <StyledHeroSection>
      <div className={`hero-content ${isLoaded ? 'loaded' : ''}`}>
        <div className="hero-name">
          <h2 className="big-heading">Mirza Waleed</h2>
        </div>
        <div className="hero-subtitle">
          <h3 className="big-heading">I build geospatial solutions for a resilient planet.</h3>
        </div>
        <div className="hero-cta">
          <a
            className="email-link"
            href="https://www.linkedin.com/in/waleedgeo"
            target="_blank"
            rel="noreferrer">
            Let's Connect @LinkedIn
          </a>
        </div>
      </div>
    </StyledHeroSection>
  );
};

export default Hero;
